import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

export class CacheTarget {
	method: string;
	routeFragments: (string | RegExp)[];
	cacheDuration?: number;
}

@Injectable({ providedIn: 'root' })
export class CacheService {
	// Cache will now store both the HttpEvent and the timestamp
	private cache: Record<string, Record<string, { event: HttpEvent<any>; timestamp: number }>> = {};
	private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

	private cacheTargets: CacheTarget[] = [];
	private poisonousTargets: CacheTarget[] = [];

	public installConfig(cacheTargets: CacheTarget[], poisonousTargets: CacheTarget[] = []) {
		this.cacheTargets.push(...cacheTargets);
		this.poisonousTargets.push(...poisonousTargets);
	}

	public get(method: string, url: string, hash: string, duration: number = this.CACHE_DURATION) {
		const key = `${method}-${encodeURI(url)}`;
		const cachedItem = this.cache?.[key]?.[hash];

		if (cachedItem) {
			const currentTime = Date.now();
			// Check if the cached item is older than the cache duration
			if (currentTime - cachedItem.timestamp < duration) {
				return cachedItem.event;
			} else {
				// If the cached item is too old, remove it
				delete this.cache[key][hash];
			}
		}
		return;
	}

	public put(method: string, url: string, hash: string, value: HttpEvent<any>) {
		const key = `${method}-${encodeURI(url)}`;
		if (!this.cache[key]) {
			this.cache[key] = {};
		}
		this.cache[key][hash] = { event: value, timestamp: Date.now() };
	}

	public clear() {
		this.cache = {};
	}

	public canCache(method: string, url: string, targetList: CacheTarget[] = this.cacheTargets): [boolean, number | undefined] {
		for (const group of targetList) {
			let match = true;
			if (group.method === method) {
				for (const segment of group.routeFragments) {
					if (!this.checkSegment(segment, url)) {
						match = false;
					}
				}
				if (match) {
					return [true, group.cacheDuration];
				}
			}
		}
		return [false, undefined];
	}

	public isPoisonous(method: string, url: string) {
		// First check if the URL should be cached
		// allow dumping cache also from non-cashable URL
		/*
		if (this.canCache(method, url)) {
			return false;
		}
		 */

		for (const group of this.poisonousTargets) {
			let match = true;
			if (group.method === method) {
				for (const segment of group.routeFragments) {
					if (!this.checkSegment(segment, url)) {
						match = false;
					}
				}
				if (match) {
					return true;
				}
			}
		}
		return false;
	}

	private checkSegment(segment: string | RegExp, url: string): boolean {
		if (typeof segment === 'string') {
			return url.includes(segment);
		} else {
			const regex = new RegExp(segment);
			return regex.test(url);
		}
	}
}
